import { sheetMaterialLines } from "../simulationStudy.js";
import { initialColorDetails } from "../costSheetColorPresets.js";
import { snapshotMaterialColors } from "../materialColors.js";
import { supabase } from "@/lib/supabase";
import {
  calculateMaterialLineCost,
  calculateProcessLineCost,
  fetchCostSheets,
  fetchManufacturingProduct,
} from "@/lib/api/manufacturing";
import { SIMULATION_SIZES } from "@/lib/constants";

async function runQuery(query, fallback = []) {
  try {
    const { data, error } = await query;
    if (error) throw error;
    return { data: data ?? fallback, error: null };
  } catch (error) {
    console.error("Manufacturing simulation API error:", error);
    return { data: fallback, error };
  }
}

export function selectSimulationCostSheet(sheets = []) {
  return sheets[0] || null;
}

const SIMULATION_SIZE_SET = new Set(SIMULATION_SIZES);
const normalizeCode = (value) => String(value || "").trim().toUpperCase();

const INVENTORY_PAGE_SIZE = 1000;
const ALFATEX_PRODUCT_PAGE_SIZE = 1000;

async function fetchAllPendingInventory() {
  const rows = [];
  for (let offset = 0; ; offset += INVENTORY_PAGE_SIZE) {
    const result = await runQuery(
      supabase
        .from("inventory")
        .select("product_id, size, color, quantity, pending_orders")
        .gt("pending_orders", 0)
        .order("product_id", { ascending: true })
        .range(offset, offset + INVENTORY_PAGE_SIZE - 1)
    );
    if (result.error) return result;
    const page = result.data || [];
    rows.push(...page);
    if (page.length < INVENTORY_PAGE_SIZE) break;
  }
  return { data: rows, error: null };
}

export async function fetchManufacturingForecastFromSales() {
  const inventoryResult = await fetchAllPendingInventory();
  if (inventoryResult.error) return { data: null, error: inventoryResult.error };

  const inventoryRows = (inventoryResult.data || []).filter((row) => {
    const missing = Number(row.pending_orders || 0) - Number(row.quantity || 0);
    return row.product_id && missing > 0;
  });

  if (!inventoryRows.length) {
    return { data: { matched: [], unmatched: [], discardedSizesSummary: [] }, error: null };
  }

  const productIds = [...new Set(inventoryRows.map((row) => row.product_id))];

  const productsResult = await runQuery(
    supabase
      .from("products")
      .select("id, code, description, temporada, composicion")
      .in("id", productIds)
  );
  if (productsResult.error) return { data: null, error: productsResult.error };
  const productsById = new Map((productsResult.data || []).map((product) => [product.id, product]));

  const neededCodes = [...new Set(
    (productsResult.data || []).map((product) => product.code).filter(Boolean)
  )];

  // Targeted lookups instead of loading the full mfg_products table (which can
  // exceed Supabase's default 1000-row cap and silently truncate results).
  const [mfgBySalesIdResult, mfgByCodeResult] = await Promise.all([
    runQuery(supabase.from("mfg_products").select("id, sales_product_id, code, status").in("sales_product_id", productIds)),
    neededCodes.length
      ? runQuery(supabase.from("mfg_products").select("id, sales_product_id, code, status").in("code", neededCodes))
      : Promise.resolve({ data: [], error: null }),
  ]);
  if (mfgBySalesIdResult.error) return { data: null, error: mfgBySalesIdResult.error };
  if (mfgByCodeResult.error) return { data: null, error: mfgByCodeResult.error };

  const mfgBySalesId = new Map();
  for (const mfg of mfgBySalesIdResult.data || []) {
    if (mfg.sales_product_id) mfgBySalesId.set(mfg.sales_product_id, mfg);
  }
  const mfgByCode = new Map();
  for (const mfg of mfgByCodeResult.data || []) {
    if (mfg.code) mfgByCode.set(normalizeCode(mfg.code), mfg);
  }

  const grouped = new Map();
  for (const row of inventoryRows) {
    const missing = Number(row.pending_orders || 0) - Number(row.quantity || 0);
    if (missing <= 0) continue;
    let group = grouped.get(row.product_id);
    if (!group) {
      group = { productId: row.product_id, cells: [], discardedSizes: new Set() };
      grouped.set(row.product_id, group);
    }
    if (SIMULATION_SIZE_SET.has(row.size)) {
      group.cells.push({ color: row.color, size: row.size, quantity: missing });
    } else {
      group.discardedSizes.add(row.size);
    }
  }

  const matched = [];
  const unmatched = [];
  const discardedSizesSummary = [];

  for (const group of grouped.values()) {
    const salesProduct = productsById.get(group.productId) || null;
    const mfg =
      mfgBySalesId.get(group.productId) ||
      (salesProduct?.code ? mfgByCode.get(normalizeCode(salesProduct.code)) : null) ||
      null;

    if (group.discardedSizes.size) {
      discardedSizesSummary.push({
        code: salesProduct?.code || "",
        description: salesProduct?.description || "",
        temporada: salesProduct?.temporada || "",
        sizes: [...group.discardedSizes],
      });
    }

    if (!mfg) {
      const totalMissing = group.cells.reduce((sum, cell) => sum + cell.quantity, 0);
      unmatched.push({
        salesProductId: group.productId,
        code: salesProduct?.code || "",
        description: salesProduct?.description || "",
        temporada: salesProduct?.temporada || "",
        totalMissing,
      });
      continue;
    }

    if (!group.cells.length) continue;

    const quantities = {};
    const colorSet = [];
    const seenColors = new Set();
    for (const cell of group.cells) {
      quantities[cellKey(cell.color, cell.size)] = cell.quantity;
      if (!seenColors.has(cell.color)) {
        seenColors.add(cell.color);
        colorSet.push(cell.color);
      }
    }
    const totalMissing = group.cells.reduce((sum, cell) => sum + cell.quantity, 0);

    matched.push({
      mfgProductId: mfg.id,
      salesProductId: group.productId,
      code: salesProduct?.code || mfg.code || "",
      description: salesProduct?.description || "",
      temporada: salesProduct?.temporada || "",
      colors: colorSet,
      quantities,
      totalMissing,
      discardedSizes: [...group.discardedSizes],
    });
  }

  matched.sort((left, right) => left.code.localeCompare(right.code));
  unmatched.sort((left, right) => left.code.localeCompare(right.code));

  return { data: { matched, unmatched, discardedSizesSummary }, error: null };
}

export async function fetchAlfatexSimulationProducts({ temporada = "", codFam = "", status = "active" } = {}) {
  const season = temporada?.trim();
  const family = codFam?.trim();
  if (!season || !family) return { data: [], error: null };

  const rows = [];
  for (let offset = 0; ; offset += ALFATEX_PRODUCT_PAGE_SIZE) {
    let query = supabase
      .from("mfg_products")
      .select("id, sales_product_id, code, description, cod_fam, family:familia!mfg_products_cod_fam_fkey(cod_fam, descrip), temporada, season:mfc_temporada!mfg_products_temporada_fkey(cod_tem, descrip), composicion, sizes, colors, status, images, main_image_index")
      .eq("temporada", season)
      .eq("cod_fam", family);

    if (status && status !== "all") query = query.eq("status", status);

    query = query
      .order("code", { ascending: true })
      .range(offset, offset + ALFATEX_PRODUCT_PAGE_SIZE - 1);

    const result = await runQuery(query);
    if (result.error) return { data: null, error: result.error };
    const page = result.data || [];
    rows.push(...page);
    if (page.length < ALFATEX_PRODUCT_PAGE_SIZE) break;
  }

  return { data: rows, error: null };
}

export async function fetchSimulationProduct(productId) {
  if (!productId) return { data: null, error: null };
  const productResult = await fetchManufacturingProduct(productId);
  if (productResult.error) return productResult;
  const sheetsResult = await fetchCostSheets(productId);
  if (sheetsResult.error) return { data: null, error: sheetsResult.error };
  const costSheet = selectSimulationCostSheet(sheetsResult.data || []);
  return {
    data: { product: productResult.data, costSheet, hasValidatedCostSheet: costSheet?.validation_status === "validated" },
    error: null,
  };
}

function cellKey(color, size) {
  return `${color}::${size}`;
}

export function normalizeSimulationQuantities(colors = [], quantities = {}) {
  const normalized = {};
  for (const color of colors) {
    for (const size of SIMULATION_SIZES) {
      const value = Number(quantities[cellKey(color, size)] || 0);
      normalized[cellKey(color, size)] = Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
    }
  }
  return normalized;
}

export function buildCostLines(sheet, totalQuantity) {
  // Visibility in manufacturing never removes a cost from the snapshot.
  const processLines = (sheet?.process_lines || []).map((line) => ({
    line_type: "process",
    source_line_id: line.id,
    name_snapshot: line.process?.description || "Proceso",
    unit: null,
    quantity_per_unit: Number(line.quantity || 0),
    waste_pct: 0,
    unit_cost: Number(line.unit_cost || 0),
    total_cost: Number((Number(line.line_cost ?? calculateProcessLineCost(line)) * totalQuantity).toFixed(4)),
    sort_order: Number(line.sort_order || 0),
    supplier_name_snapshot: line.supplier?.name || null,
    excludes_manufacturing: Boolean(line.process?.excludes_manufacturing),
    is_overhead: Boolean(line.process?.is_overhead),
  }));
  const materialEntries = sheetMaterialLines(sheet || {}).map((line) => {
    const parent = (sheet?.process_lines || []).find((process) => process.id === line.process_line_id
      || process.material_lines?.some((material) => material === line || (line.id && material.id === line.id)));
    return { line, processLineId: parent?.id || line.process_line_id || null,
      processName: parent?.process?.description || null,
      excluded: Boolean(parent?.process?.excludes_manufacturing),
      overhead: Boolean(parent?.process?.is_overhead) };
  });

  const materialLines = materialEntries.map(({ line, processLineId, processName, excluded, overhead }) => ({
    line_type: "material",
    source_line_id: line.id,
    name_snapshot: line.material?.internal_description || line.material?.code || "Material",
    unit: line.unit || line.material?.default_unit || null,
    quantity_per_unit: Number(line.quantity || 0),
    waste_pct: Number(line.waste_pct || 0),
    unit_cost: Number(line.unit_cost || 0),
    total_cost: Number((Number(line.line_cost ?? calculateMaterialLineCost(line)) * totalQuantity).toFixed(4)),
    sort_order: Number(line.sort_order || 0),
    excludes_manufacturing: excluded,
    is_overhead: overhead,
    process_source_line_id: processLineId,
    process_name_snapshot: processName,
    supplier_name_snapshot: line.material_supplier?.supplier?.name || null,
  }));

  return [...processLines, ...materialLines];
}

export function calculateSimulationProduct(product, costSheet, quantities = {}, details = {}) {
  const simulatedColors = details.simulated_colors || [];
  const normalized = normalizeSimulationQuantities(simulatedColors, quantities);
  const quantityRows = Object.entries(normalized)
    .filter(([, quantity]) => quantity > 0)
    .map(([key, quantity]) => {
      const [color, size] = key.split("::");
      return { color, size, quantity };
    });
  const totalQuantity = quantityRows.reduce((sum, row) => sum + row.quantity, 0);
  const costLines = buildCostLines(costSheet, totalQuantity);
  const unitLines = buildCostLines(costSheet, 1);
  const overheadLines = costLines.filter(line => line.is_overhead);
  const unitProcessCost = Number(unitLines.filter(line => line.line_type === "process" && !line.is_overhead).reduce((sum, line) => sum + line.total_cost, 0).toFixed(4));
  const unitMaterialCost = Number(unitLines.filter(line => line.line_type === "material" && !line.is_overhead).reduce((sum, line) => sum + line.total_cost, 0).toFixed(4));
  const unitTotalCost = Number((unitProcessCost + unitMaterialCost).toFixed(4));

  return {
    item: totalQuantity > 0 ? {
      product_id: product.id,
      cost_sheet_id: costSheet?.id || null,
      product_code_snapshot: product.code,
      description_snapshot: product.description,
      manufacturer_reference: details.manufacturer_reference?.trim() || null,
      pattern_code: details.pattern_code?.trim() || null,
      season_snapshot: details.season_snapshot?.trim() || product.temporada || null,
      notes: details.notes?.trim() || null,
      unit_process_cost: unitProcessCost,
      unit_material_cost: unitMaterialCost,
      unit_total_cost: unitTotalCost,
      total_quantity: totalQuantity,
      total_process_cost: Number((unitProcessCost * totalQuantity).toFixed(4)),
      total_material_cost: Number((unitMaterialCost * totalQuantity).toFixed(4)),
      total_cost: Number((unitTotalCost * totalQuantity).toFixed(4)),
      quantities: quantityRows,
      color_lines: simulatedColors.map((color, index) => ({
        color,
        variant_id: details.color_variants?.[color]?.variant_id || null,
        garment_color: details.color_variants?.[color]?.garment_color || color,
        material_colors: snapshotMaterialColors(costSheet, details.color_variants?.[color]?.material_colors),
        contrast_1: details.color_contrasts?.[color]?.contrast_1?.trim() || null,
        contrast_2: details.color_contrasts?.[color]?.contrast_2?.trim() || null,
        contrast_3: details.color_contrasts?.[color]?.contrast_3?.trim() || null,
        quantity_p: normalized[cellKey(color, "P")],
        quantity_m: normalized[cellKey(color, "M")],
        quantity_l: normalized[cellKey(color, "L")],
        quantity_xl: normalized[cellKey(color, "XL")],
        quantity_2xl: normalized[cellKey(color, "2XL")],
        quantity_3xl: normalized[cellKey(color, "3XL")],
        quantity_4xl: normalized[cellKey(color, "4XL")],
        quantity_5xl: normalized[cellKey(color, "5XL")],
        sort_order: index,
      })),
      cost_lines: costLines,
    } : null,
    totals: {
      overhead_lines: overheadLines,
      total_quantity: totalQuantity,
      process_total: Number((unitProcessCost * totalQuantity).toFixed(4)),
      material_total: Number((unitMaterialCost * totalQuantity).toFixed(4)),
      grand_total: Number((unitTotalCost * totalQuantity).toFixed(4)),
    },
  };
}

export function summarizeSimulationItems(items = []) {
  const validItems = items.filter(Boolean);
  return {
    items: validItems,
    totals: {
      overhead_lines: validItems.flatMap(item => (item.cost_lines || []).filter(line => line.is_overhead).map(line => ({ ...line, article: item.product_code_snapshot }))),
      total_quantity: Number(validItems.reduce((sum, item) => sum + Number(item.total_quantity || 0), 0).toFixed(4)),
      process_total: Number(validItems.reduce((sum, item) => sum + Number(item.total_process_cost || 0), 0).toFixed(4)),
      material_total: Number(validItems.reduce((sum, item) => sum + Number(item.total_material_cost || 0), 0).toFixed(4)),
      grand_total: Number(validItems.reduce((sum, item) => sum + Number(item.total_cost || 0), 0).toFixed(4)),
    },
  };
}

export async function buildForecastSimulationItems(entries = [], { onProgress } = {}) {
  const items = [];
  const total = entries.length;
  for (let index = 0; index < total; index += 1) {
    const entry = entries[index];
    onProgress?.({ current: index, total, code: entry.code || "" });
    const result = await fetchSimulationProduct(entry.mfgProductId);
    if (result.error) return { data: null, error: result.error };
    const product = result.data?.product;
    if (!product) continue;
    const costSheet = result.data?.costSheet || null;
    const details = {
      manufacturer_reference: "",
      pattern_code: "",
      season_snapshot: entry.temporada || product.temporada || "",
      notes: "",
      color_contrasts: {},
      simulated_colors: entry.colors || [],
      color_variants: initialColorDetails(costSheet).color_variants,
    };
    const calc = calculateSimulationProduct(product, costSheet, entry.quantities || {}, details);
    if (calc.item) items.push(calc.item);
  }
  onProgress?.({ current: total, total, code: "" });
  return { data: items, error: null };
}

const simulationSelect = "*, items:mfg_simulation_items(*, color_lines:mfg_simulation_color_lines(*), cost_lines:mfg_simulation_cost_lines(*), product:mfg_products(id, code, description, images, main_image_index, temporada, composicion))";

export async function fetchSimulationHistory({ status = "saved", limit = 100, articleSearch = "" } = {}) {
  const search = articleSearch.trim();
  // Filter through a separate relation so grouped simulations retain ALL items.
  const historySelect = `${simulationSelect},production_orders:mfg_production_orders(id)`;
  const select = search ? `${historySelect},matched_items:mfg_simulation_items!inner()` : historySelect;
  let query = supabase.from("mfg_simulation_runs").select(select).order("simulation_date", { ascending: false }).order("created_at", { ascending: false });
  if (search) {
    const pattern = JSON.stringify(`%${search}%`);
    query = query.or(`product_code_snapshot.ilike.${pattern},description_snapshot.ilike.${pattern}`, { referencedTable: "matched_items" });
  }
  if (status && status !== "all") query = query.eq("status", status);
  if (limit) query = query.limit(limit);
  const result = await runQuery(query);
  if (result.error || !result.data.length) return result;

  // Load provenance separately: self-referencing embeds depend on relationships
  // exposed by PostgREST's schema cache and can prevent the entire list loading.
  const parentIds = [...new Set(result.data.map(row => row.parent_simulation_id).filter(Boolean))];
  const [origins, splits] = await Promise.all([
    parentIds.length
      ? runQuery(supabase.from("mfg_simulation_runs").select("id,description").in("id", parentIds))
      : Promise.resolve({ data: [], error: null }),
    runQuery(supabase.from("mfg_simulation_splits").select("source_id,destination_id").in("source_id", result.data.map(row => row.id))),
  ]);
  const originsById = new Map(origins.data.map(row => [row.id, row]));
  const splitsBySource = new Map();
  for (const split of splits.data) {
    if (!splitsBySource.has(split.source_id)) splitsBySource.set(split.source_id, []);
    splitsBySource.get(split.source_id).push(split);
  }
  return {
    ...result,
    data: result.data.map(row => ({ ...row, origin: originsById.get(row.parent_simulation_id) || null, splits: splitsBySource.get(row.id) || [] })),
  };
}

export async function fetchSimulationsByProduct(productId, { status = "saved", limit = 100 } = {}) {
  if (!productId) return { data: [], error: null };
  const select = "*, items:mfg_simulation_items!inner(*, color_lines:mfg_simulation_color_lines(*), product:mfg_products(id, code, description, images, main_image_index, temporada, composicion))";
  let query = supabase
    .from("mfg_simulation_runs")
    .select(select)
    .eq("items.product_id", productId)
    .order("simulation_date", { ascending: false })
    .order("created_at", { ascending: false });
  if (status && status !== "all") query = query.eq("status", status);
  if (limit) query = query.limit(limit);
  return runQuery(query);
}

export async function fetchManufacturingSimulation(id) {
  if (!id) return { data: null, error: new Error("Simulacion no valida") };
  return runQuery(supabase.from("mfg_simulation_runs").select(simulationSelect).eq("id", id).single(), null);
}

export async function saveManufacturingSimulation({ id = null, expectedVersion = null, simulationDate, description, mode = "group", notes = "", items = [], totals = {} }) {
  const simulationItems = items.filter((item) => Number(item.total_quantity || 0) > 0);
  if (!description?.trim()) return { data: null, error: new Error("La descripcion es obligatoria") };
  if (simulationItems.length === 0) return { data: null, error: new Error("La simulacion no contiene cantidades") };

  const header = {
    simulation_date: simulationDate || new Date().toISOString().slice(0, 10),
    description: description.trim(),
    mode,
    status: "saved",
    notes: notes?.trim() || null,
    ...totals,
    expected_version: expectedVersion,
  };
  return runQuery(supabase.rpc("save_mfg_simulation_transaction", {
    p_id: id, p_header: header, p_items: simulationItems,
  }), null);
}

export async function deleteManufacturingSimulation(id, expectedVersion) {
  if (!id) return { data: null, error: new Error("Simulacion no valida") };
  return runQuery(supabase.rpc("delete_mfg_simulation", { p_id: id, p_expected_version: expectedVersion }), null);
}

export async function archiveManufacturingSimulation(id, expectedVersion) {
  if (!id) return { data: null, error: new Error("Simulacion no valida") };
  return runQuery(supabase.rpc("archive_mfg_simulation", { p_id: id, p_expected_version: expectedVersion }), null);
}

export async function splitManufacturingSimulation({ sourceId, expectedVersion, itemIds, description, simulationDate, notes, operationId }) {
  return runQuery(supabase.rpc("split_mfg_simulation_transaction", {
    p_source_id: sourceId,
    p_expected_version: expectedVersion,
    p_item_ids: itemIds,
    p_header: { description: description.trim(), simulation_date: simulationDate, notes: notes?.trim() || null },
    p_operation_id: operationId,
  }), null);
}
